import { useState, useCallback, useMemo, useEffect, useRef, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Radio, Pagination, Form, Table } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { TableHeaderCol } from '@/facilities/model/user';
import dayjs from '@/utils/dayjsConfig';
import {
  TC,
  TABLEPAI,
  divClass,
  EIMIT,
  MONTH_PARMS,
  SUMMARY_REPORT_FILTERS,
  BREAKDOWN_CODE_SUMMARY_REPORTS_TABLE_HEADER,
  BREAKDOWN_CODE_SUMMARY_REPORTS_SORT,
} from '@/enums/pageEnum';
import {
  ReportTitle,
  CollapseFilters,
  WindowPrint,
  PrintContent,
  PrintButton,
} from '@/pages/report/components';
import { FilterComponet, ExportFile, CustomModal } from '@/components';
import { FilterParams } from '@/facilities/model/user';
import type { ProFormInstance } from '@ant-design/pro-components';
import { useAppSelector } from '@/store';
import { useUtilsMemo } from '@/hooks';
import { getTableHeaderConfig, updateReportSort, getList, getProperOrg } from '@api';
import { parseQueryString, parseMonth } from '@/utils/toolt';
import { useLocation } from 'react-router-dom';
import useMessageRedux from '@/store/message-redux';
import { uniqBy, isString, pickBy } from 'lodash-es';

const BSEURL = '/api/reports/breakdown-code-summary';
const FCODE = 'faultCode';

interface SortByModel {
  groupBy: string;
  sortField?: string;
  sort?: string;
}

export default function BreakdownCodeSummary() {
  const orgType = useAppSelector((state) => state.app.organizationType);
  const { t } = useTranslation();
  const [filterDas, setFilterDas] = useState<any>({});
  const [sortByList, setSortByList] = useState<TableHeaderCol[]>([]);
  const formRef = useRef<ProFormInstance>(null);
  const [form] = Form.useForm();
  const { previousMonth, monthObj } = useUtilsMemo();
  const [sortbyForm, setSortbyForm] = useState<SortByModel>({
    groupBy: FCODE,
  });
  const [sortMediate, setSortMediate] = useState(FCODE);
  const location = useLocation();
  const { message } = useMessageRedux();
  const [pageParams, setPageParams] = useState({ current: 1, size: 20, total: 0 });
  const [isPending, startTransition] = useTransition();
  const [data, setData] = useState<object[]>([]);
  const [printData, setPrintData] = useState<object[]>([]);
  const getPathParams = useCallback(() => parseMonth(parseQueryString()), []);
  const isShowPrint = useMemo(() => getPathParams()?.print === 'true', [getPathParams]);

  const tcurl: any = useMemo(
    () => ({
      faultCode: `${BSEURL}/tc-fault-code/page`,
      faultCodeRange: `${BSEURL}/tc-fault-code-range/page`,
    }),
    [],
  );
  const url = useMemo(() => {
    if (orgType === TC) return tcurl[sortbyForm.groupBy];
    return `${BSEURL}/lc/all`;
  }, [sortbyForm, orgType, tcurl]);

  const getTableLists = useCallback(
    ({
      page,
      isReset = false,
      curl,
      newParams,
    }: {
      page?: number;
      isReset?: boolean;
      curl?: string;
      newParams?: SortByModel;
    }) => {
      startTransition(async () => {
        const params = getPathParams();
        try {
          let sort = null;
          if (newParams && newParams.sortField && newParams.sort) {
            sort = `${newParams.sortField},${newParams.sort}`;
          }
          if (!newParams && sortbyForm.sortField && sortbyForm.sort) {
            sort = `${sortbyForm.sortField},${sortbyForm.sort}`;
          }
          const fromParams = {
            page: page || pageParams.current,
            size: pageParams.size,
            sort,
            ...(isReset ? { month: dayjs(previousMonth).format(MONTH_PARMS) } : params),
          };
          const urlParms = new URLSearchParams(fromParams);
          if (sort && sort.includes('occurrence')) {
            urlParms.append(
              'sort',
              (newParams?.groupBy || sortbyForm.groupBy) === FCODE
                ? `faultcode,desc`
                : 'faultcoderange,desc',
            );
          }
          const res = await getList({
            url: curl || url,
            params: sort ? urlParms : fromParams,
          });
          const body = res.data.body;
          setPageParams((prev) => {
            prev.total = body?.totalElements;
            prev.current = page || 1;
            return prev;
          });
          if (orgType !== TC) {
            // 处理LC视角的数据先按照liftTc和faultCode进行分组, liftTc和faultCode相同的数据分一组
            const townCouncil = body?.reduce((acc: any, item: any) => {
              const tcKey = `${item.liftTc}-${item.faultCode}`;
              if (!acc[tcKey]) {
                acc[tcKey] = [];
              }
              acc[tcKey].push({ ...item, tcKey });
              return acc;
            }, {});
            // 根据Occurrence进行排序
            const dataFoatm = Object.keys(townCouncil).map((key: string) => {
              const groupLifts = townCouncil[key];
              const groupLift = groupLifts.sort((a: any, b: any) => {
                return b.occurrence - a.occurrence;
              });
              // 获取Occurrence组成员的总和
              const occurrenceSum = groupLift.reduce((sum: number, item: any) => {
                return sum + item.occurrence;
              }, 0);
              const firstItem = groupLift[0] || {};
              return {
                ...firstItem,
                totalOccurrence: occurrenceSum,
                liftBrands: groupLifts,
              };
            });
            // 对dataFoatm数组的totalOccurrence进行降序排序, 如果相等,则按照faultCode进行升序排序
            dataFoatm.sort((a: any, b: any) => {
              if (b.totalOccurrence !== a.totalOccurrence) {
                return b.totalOccurrence - a.totalOccurrence;
              }
              return a.faultCode.localeCompare(b.faultCode);
            });
            setPrintData(dataFoatm);
            setData(dataFoatm);
            return;
          }
          if (isShowPrint && body?.content.length === pageParams.size) {
            getTableLists({ page: pageParams.current + 1 });
          }
          setPrintData((prev) =>
            uniqBy([...prev, ...body.content], newParams?.groupBy || sortbyForm.groupBy),
          );
          setData(body.content);
        } catch (error: any) {
          message.error(isString(error) ? error : error.detail || JSON.stringify(error));
        }
      });
    },
    [
      getPathParams,
      message,
      pageParams,
      isShowPrint,
      url,
      orgType,
      sortbyForm,
      previousMonth,
    ],
  );

  const resetFilters = useCallback(
    ({ page, isReset }: { page?: number; isReset?: boolean }) => {
      setPageParams((prev) => {
        prev.current = page || 1;
        return prev;
      });
      getTableLists({ page, isReset });
    },
    [getTableLists],
  );

  const menuItemOnClick = useCallback(
    (val: any) => {
      if (val && val.detail.key !== location.pathname) return;
      getTableLists({ page: 1, isReset: true });
    },
    [location.pathname, getTableLists],
  );

  useEffect(() => {
    document.addEventListener(EIMIT, menuItemOnClick);
    return () => {
      document.removeEventListener(EIMIT, menuItemOnClick);
    };
  }, [menuItemOnClick]);

  const getProperties = useCallback(() => {
    startTransition(async () => {
      try {
        if (orgType === TC) {
          const res = await getTableHeaderConfig(
            BREAKDOWN_CODE_SUMMARY_REPORTS_TABLE_HEADER,
          );
          const userCofig = await getList({
            url: TABLEPAI.USER_PROPERTS,
            params: { type: BREAKDOWN_CODE_SUMMARY_REPORTS_SORT },
          });
          const body = userCofig.data.body;
          const groupBy = body.body?.groupBy || FCODE;
          setSortbyForm({ ...body.body, groupBy });
          setSortMediate(groupBy);
          getTableLists({ curl: tcurl[groupBy], newParams: body.body });
          form?.setFieldsValue({ ...body.body, groupBy });
          setSortByList(res.data?.body?.body || []);
        } else {
          const resOrg = await getProperOrg({ type: SUMMARY_REPORT_FILTERS });
          setFilterDas(resOrg.data?.body?.body || {});
          getTableLists({});
        }
      } catch (error: any) {
        message.error(isString(error) ? error : error.detail || JSON.stringify(error));
      }
    });
  }, [message, orgType, form, tcurl]);

  const sortByMe = useMemo(() => {
    const options: TableHeaderCol[] = [];
    sortByList.forEach((item: TableHeaderCol) => {
      if (
        sortMediate.toLowerCase() === item.sortField ||
        item.sortField === 'occurrence'
      ) {
        options.push({
          ...item,
          label: t(item.sortField!),
          key: item.sortField!,
        });
      }
    });
    return options;
  }, [sortByList, sortMediate, t]);

  useEffect(() => {
    getProperties();
  }, [getProperties]);

  const filtersFileds: FilterParams[] = useMemo(() => {
    const month = monthObj(() => {});
    if (orgType === TC) return [month];
    return [
      month,
      {
        component: 'cSel' as const,
        field: 'townCouncil',
        label: t('TOWN_COUNCIL'),
        dataSource: filterDas?.townCouncil,
        fieldProps: { divClass },
      },
    ];
  }, [t, filterDas, orgType, monthObj]);

  const tableTitle = useMemo(() => {
    const parms = getPathParams();
    const month = dayjs(parms.month || previousMonth).format('MMM YYYY');
    return `${month} ${t('breakdownCode')}`;
  }, [t, getPathParams, previousMonth]);

  const isPrintLading = useMemo(() => {
    if (orgType !== TC) return printData.length > 0 && !isPending;
    return printData.length > 0 && printData.length === pageParams.total && !isPending;
  }, [printData, isPending, pageParams, orgType]);

  const columns = useMemo(() => {
    if (orgType !== TC) {
      return [
        {
          title: t('TOWN_COUNCIL'),
          dataIndex: 'liftTc',
          renderText: (text: string) => <span className="text-[#AB3E3E]">{text}</span>,
        },
        { title: t('faultCo'), dataIndex: FCODE },
        { title: t('description'), dataIndex: 'faultCodeDescription' },
        { title: t('occurrence'), dataIndex: 'totalOccurrence' },
      ];
    }
    const obj = [
      { title: t('classifOfbran'), dataIndex: 'faultCodeGrouping' },
      { title: t('occurrence'), dataIndex: 'occurrence' },
    ];
    if (sortbyForm.groupBy === 'faultCodeRange') {
      return [{ title: t('faultCodeRange'), dataIndex: 'faultCodeRange' }, ...obj];
    }
    return [
      { title: t('faultCo'), dataIndex: FCODE },
      { title: t('description'), dataIndex: 'faultCodeDescription' },
      ...obj,
    ];
  }, [t, sortbyForm, orgType]);

  const getPrintContent = useCallback(
    (d: object[]) => {
      return (
        <PrintContent
          columns={columns}
          loading={isShowPrint ? false : isPending}
          tableTitle={tableTitle}
          dataSource={d}
          expandable={
            orgType !== TC
              ? {
                  expandedRowRender: (record) => {
                    return (
                      <Table
                        columns={[
                          { title: t('liftBrand'), dataIndex: 'liftBrand', width: 300 },
                          { title: t('occurrence'), dataIndex: 'occurrence' },
                        ]}
                        rowKey={'liftBrand'}
                        dataSource={record.liftBrands}
                        pagination={false}
                      />
                    );
                  },
                  expandRowByClick: true,
                  defaultExpandAllRows: isShowPrint,
                }
              : undefined
          }
          rowKey={orgType !== TC ? 'tcKey' : sortbyForm.groupBy}
          className="border-[#F1F0F5] dark:border-[#ffffff21] border-[1px] caseTable"
        />
      );
    },
    [tableTitle, columns, isPending, isShowPrint, sortbyForm, orgType, t],
  );

  const fautOptions = useMemo(
    () => [
      { label: t('faultCo'), value: FCODE },
      { label: t('faultCodeRange'), value: 'faultCodeRange' },
    ],
    [t],
  );

  const sortOptions = useMemo(
    () => [
      { value: 'ASC', label: t('ascending') },
      { value: 'DESC', label: t('descending') },
    ],
    [t],
  );

  if (isShowPrint) {
    return (
      <WindowPrint isPrintLading={isPrintLading}>
        {getPrintContent(printData)}
      </WindowPrint>
    );
  }

  return (
    <div className="p-4">
      <ReportTitle title={t('breakdownCode')} />
      <CollapseFilters>
        <FilterComponet
          className="mb-[2px] filtersFromClass !py-0 !pl-0"
          layout="horizontal"
          submitNode={<>{t('preview')}</>}
          fieldsProp={filtersFileds}
          ref={formRef}
          submit={() => {
            resetFilters({});
          }}
          reset={() => {
            resetFilters({});
          }}
        />
      </CollapseFilters>
      <div className="mt-4 p-4 bg-[#fff] dark:bg-[#141414]">
        <div className="flex justify-between items-center pb-4 border-b-[1px] border-[#E8E9ED] dark:border-[#ffffff21]">
          <div className="flex justify-between items-center">
            <ExportFile
              disabled={!data.length}
              onClick={() => {
                const { origin } = new URL(window.location.href);
                const downurl = new URLSearchParams({ ...getPathParams() });
                window.open(
                  `${origin}${BSEURL}/${orgType === TC ? 'tc' : 'lc'}/export?${downurl?.toString()}`,
                );
              }}
            />
            <PrintButton disabled={!data.length} buttonName={t('print')} />
          </div>
          {orgType === TC && (
            <CustomModal
              title={t('customize')}
              isProForm={false}
              okText={t('confirm')}
              width={520}
              modalName={t('hint.cusReport')}
              className="cusReportModal"
              buttonCogfig={{ variant: 'outlined', icon: <SettingOutlined /> }}
              onColseModal={() => {
                form.resetFields();
              }}
              onShowModal={() => {
                setSortMediate(sortbyForm.groupBy);
                form.setFieldsValue({ ...sortbyForm });
              }}
              onClick={async (_, callBack) => {
                startTransition(async () => {
                  try {
                    const formData = form.getFieldsValue();
                    const sortParms = pickBy(formData);
                    const reult = sortParms.sortField
                      ? !sortParms.sort
                        ? { ...sortParms, sort: 'DESC' }
                        : sortParms
                      : { groupBy: formData.groupBy };
                    await updateReportSort({
                      type: BREAKDOWN_CODE_SUMMARY_REPORTS_SORT,
                      body: reult,
                    });
                    setSortbyForm(formData);
                    setSortMediate(formData.groupBy);
                    getTableLists({
                      page: 1,
                      curl: tcurl[formData.groupBy],
                      newParams: { ...formData, ...reult },
                    });
                    callBack?.(false);
                  } catch (error) {
                    message.error(JSON.stringify(error));
                    callBack?.(true);
                  }
                });
              }}
            >
              <h4 className="text-[16px] font-light my-3">{t('groupSort')}</h4>
              <div className="bg-[#F4F4F4] dark:bg-[#141414] mb-3 px-5 pt-3">
                <Form layout="horizontal" form={form}>
                  <div className="text-[14px] mb-4">{t('groupBy')}: </div>
                  <Form.Item label="" name="groupBy" className="!mb-4 flatGroup">
                    <Radio.Group
                      options={fautOptions}
                      onChange={(e) => {
                        if (e.target.value !== sortbyForm.groupBy) {
                          form.setFieldsValue({ sortField: null, sort: null });
                        } else {
                          form.setFieldsValue({
                            sortField: sortbyForm.sortField,
                            sort: sortbyForm.sort,
                          });
                        }
                        setSortMediate(e.target.value);
                      }}
                    />
                  </Form.Item>
                  <div className="flex justify-between items-center">
                    <Form.Item className="w-[320px]" label={t('sortBy')} name="sortField">
                      <Select
                        options={sortByMe}
                        fieldNames={{ value: 'sortField' }}
                        placeholder={t('Please select')}
                      />
                    </Form.Item>
                    <Form.Item className="w-[120px]" label="" name="sort">
                      <Select placeholder={t('descending')} options={sortOptions} />
                    </Form.Item>
                  </div>
                </Form>
              </div>
            </CustomModal>
          )}
        </div>
        {getPrintContent(data)}
        {pageParams.total > pageParams.size && (
          <div className="pt-4">
            <Pagination
              align="end"
              onChange={(page) => {
                resetFilters({ page });
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              current={pageParams.current}
              pageSize={pageParams.size}
              total={pageParams.total}
              showSizeChanger={false}
            />
          </div>
        )}
      </div>
    </div>
  );
}
